import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "wouter";
import {
  Search,
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Activity,
  Tag,
  BarChart2,
  Megaphone,
  LayoutGrid,
  MessageSquare,
  Image as ImageIcon,
  Compass,
  Rocket,
  Layers,
  Wand2,
  PenTool,
  Bookmark,
  User,
  CreditCard,
  Settings,
  Keyboard,
  HelpCircle,
  CheckCircle2,
  Lightbulb,
  AlertCircle,
  Camera,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";

// ---------- Types ----------

type Block =
  | { type: "para"; text: string }
  | { type: "heading"; text: string }
  | { type: "list"; items: string[] }
  | { type: "ordered"; items: string[] }
  | { type: "screenshot"; caption: string; src?: string; aspect?: "video" | "wide" | "square" }
  | { type: "tip"; text: string }
  | { type: "note"; text: string }
  | { type: "warn"; text: string }
  | { type: "shortcut"; keys: string[]; description: string }
  | { type: "feature"; title: string; description: string }
  | { type: "divider" };

type Section = {
  id: string;
  title: string;
  icon: LucideIcon;
  group: string;
  intro: string;
  blocks: Block[];
};

// ---------- Section content ----------

const SECTIONS: Section[] = [
  {
    id: "introduction",
    title: "Welcome to AdMoji",
    icon: Sparkles,
    group: "Get Started",
    intro:
      "AdMoji is an AI ad manager that helps you research, design, launch, and optimize paid social campaigns — without ever leaving the canvas.",
    blocks: [
      { type: "heading", text: "What you can do" },
      {
        type: "list",
        items: [
          "Generate ad creative from a single sentence using AI image and copy models.",
          "Research competitor ads and remix winning concepts onto your canvas.",
          "Track campaign performance across Meta and other platforms in one dashboard.",
          "Build a long-term brand brain (Heartbeat) the AI uses to keep voice and offer consistent.",
          "Launch campaigns and create variations with one chat message.",
        ],
      },
      { type: "heading", text: "The North Star" },
      {
        type: "para",
        text:
          "Our goal is simple: never touch the keyboard. You should be able to run a complete creative + media buying workflow by talking to AskMoji, the AI assistant on the right side of the canvas. Everything in this guide is built to support that.",
      },
      {
        type: "screenshot",
        caption: "The Canvas — your home base. Sidebar on the left, infinite canvas in the middle, AskMoji chat on the right.",
        src: "/docs/canvas-overview.png",
        aspect: "wide",
      },
      { type: "heading", text: "How to use these docs" },
      {
        type: "list",
        items: [
          "Use the sidebar to jump to a feature.",
          "Use the search bar at the top to find anything by keyword.",
          "Each section starts with a quick summary, then walks through how to use it.",
          "Tips are highlighted in blue. Warnings are in amber.",
        ],
      },
    ],
  },

  {
    id: "signup-and-plans",
    title: "Sign up & plans",
    icon: Rocket,
    group: "Get Started",
    intro: "Create an account, choose a plan, and land on the canvas.",
    blocks: [
      { type: "heading", text: "Creating an account" },
      {
        type: "ordered",
        items: [
          "Visit the AdMoji homepage and click Sign up.",
          "Enter your email and a password (8+ characters recommended).",
          "Confirm the account from the email we send (if email confirmation is enabled).",
          "On first login you'll be asked to choose a plan.",
        ],
      },
      {
        type: "screenshot",
        caption: "Signup page with email and password fields.",
        src: "/docs/signup.png",
        aspect: "wide",
      },
      { type: "heading", text: "Choosing a plan" },
      {
        type: "para",
        text:
          "Plans are credit-based. Each plan grants you a monthly credit balance that pays for AI image generation, AI chat, scraping, and analytics syncs. Pick the plan that matches the volume of ads and research you expect to run each month.",
      },
      {
        type: "screenshot",
        caption: "The plan picker shown to new users before their first canvas session.",
        src: "/docs/plan-picker.png",
        aspect: "wide",
      },
      {
        type: "tip",
        text:
          "You can change plans at any time from your Profile. Unused credits roll over for one billing cycle, then expire.",
      },
      { type: "heading", text: "First-run experience" },
      {
        type: "para",
        text:
          "After choosing a plan you'll land on the canvas. The first thing AskMoji will ask is which brand you want to work on. If you don't have a brand yet, type the brand name or paste your website URL — we'll do the rest.",
      },
    ],
  },

  {
    id: "canvas-overview",
    title: "The Canvas",
    icon: Compass,
    group: "Workspace",
    intro:
      "The canvas is your home. It's an infinite zoomable surface where every ad, brief, and chat message lives.",
    blocks: [
      { type: "heading", text: "Anatomy of the canvas" },
      {
        type: "list",
        items: [
          "Left sidebar — projects, embedded views (Heartbeat, Brands, Analytics, Ads Manager, Ad Library).",
          "Center workspace — your infinite canvas. Zoom with scroll, pan with the hand tool or spacebar.",
          "Top toolbar — select, hand, brush, comment, and mark tools.",
          "Right chat panel — AskMoji, your AI assistant. Drag the divider to resize.",
          "Bottom-right zoom controls — zoom in, out, fit to screen.",
        ],
      },
      {
        type: "screenshot",
        caption: "Annotated canvas layout. Sidebar (left), workspace (center), toolbar (top), chat (right).",
        src: "/docs/canvas-anatomy.png",
        aspect: "wide",
      },
      { type: "heading", text: "Projects" },
      {
        type: "para",
        text:
          "Each project is a separate canvas with its own chat history, generated images, and notes. Use projects to keep different brands, campaigns, or experiments isolated.",
      },
      {
        type: "ordered",
        items: [
          "Click the + button next to Projects in the sidebar.",
          "Give the project a name and pick a color (used for image layer borders).",
          "Optionally pin it to keep it at the top of the list.",
          "Open it from the sidebar at any time — your work auto-saves.",
        ],
      },
      {
        type: "screenshot",
        caption: "Create-project modal with name, color, and pinning options.",
        src: "/docs/create-project.png",
      },
      { type: "heading", text: "Working with images on the canvas" },
      {
        type: "list",
        items: [
          "Click an image to select it — toolbar buttons appear above for clone, edit, favorite, delete.",
          "Drag to move. Hold Shift while dragging for free positioning, or release on the alignment guides for snapping.",
          "Hold Cmd/Ctrl and click multiple images to multi-select.",
          "Press Delete or Backspace to remove selected images.",
        ],
      },
      {
        type: "tip",
        text:
          "Images stay on the canvas permanently — even after you close the app. They're tied to the project, not to a single chat session.",
      },
    ],
  },

  {
    id: "canvas-toolbar",
    title: "Toolbar & drawing tools",
    icon: PenTool,
    group: "Workspace",
    intro: "The toolbar at the top of the canvas controls how your cursor interacts with the workspace.",
    blocks: [
      { type: "heading", text: "Tool reference" },
      {
        type: "feature",
        title: "Select",
        description: "Default mode. Click images to select, drag to move, marquee-select with click and drag on empty canvas.",
      },
      {
        type: "feature",
        title: "Hand",
        description: "Pan the canvas by clicking and dragging. You can also hold the spacebar in any tool to temporarily activate pan.",
      },
      {
        type: "feature",
        title: "Brush",
        description: "Free-draw onto the canvas. Useful for sketching layout ideas or annotating areas of an image.",
      },
      {
        type: "feature",
        title: "Comment",
        description: "Click anywhere to drop a pin with a text note. Comments are tied to a position so they move if you reorganize.",
      },
      {
        type: "feature",
        title: "Mark",
        description: "Draw a rectangle on an image and add an instruction. The next AI generation will treat that region specially — for example, 'remove the hat in this box'.",
      },
      {
        type: "screenshot",
        caption: "The toolbar with tool icons and active-state highlighting.",
        src: "/docs/canvas-toolbar.png",
      },
      { type: "heading", text: "Marks vs Comments" },
      {
        type: "para",
        text:
          "Comments are notes for you and your team. Marks are instructions for the AI — when you trigger another generation pass, the AI reads the mark and applies it to the next round of images.",
      },
      {
        type: "tip",
        text:
          "Marks are the fastest way to iterate. Instead of describing changes in chat, draw a box on the exact spot and write a one-line instruction.",
      },
    ],
  },

  {
    id: "askmoji",
    title: "AskMoji chat",
    icon: MessageSquare,
    group: "Workspace",
    intro:
      "AskMoji is the AI media buyer on the right side of the canvas. Talk to it the way you'd talk to a media buyer on a call.",
    blocks: [
      { type: "heading", text: "What to type" },
      {
        type: "para",
        text:
          "AskMoji understands plain English. There are no commands to memorize. Try things like:",
      },
      {
        type: "list",
        items: [
          "\"Make me a campaign for the cordless drill.\"",
          "\"What's working in our Meta account this week?\"",
          "\"Show me competitor ads for [brand].\"",
          "\"Make 4 variations of this ad.\"",
          "\"Write 5 hooks for the back-to-school launch.\"",
        ],
      },
      {
        type: "screenshot",
        caption: "AskMoji chat panel with example prompt and a streaming response.",
        src: "/docs/askmoji-chat.png",
      },
      { type: "heading", text: "How AskMoji responds" },
      {
        type: "list",
        items: [
          "If you ask a question, AskMoji answers in chat — direct and concise, like a media buyer on a call.",
          "If you ask it to do something (\"make ads\"), it kicks off a Playbook — a multi-step workflow that runs on the canvas.",
          "If it needs more info, it'll ask — but only if it can't make a reasonable guess.",
        ],
      },
      { type: "heading", text: "The chat sidebar" },
      {
        type: "list",
        items: [
          "Drag the left edge of the chat to resize.",
          "Click the panel header to collapse the chat (the canvas gets the full width).",
          "Each project keeps its own chat history — switching projects swaps the conversation.",
        ],
      },
      {
        type: "tip",
        text:
          "AskMoji has access to your Heartbeat (knowledge graph), connected ad accounts, brand profiles, and competitor data. The more context you've added, the smarter its answers.",
      },
    ],
  },

  {
    id: "playbooks",
    title: "Playbooks",
    icon: Wand2,
    group: "Workspace",
    intro:
      "Playbooks are multi-step workflows AskMoji runs for you on the canvas. They handle research, generation, and approval in one flow.",
    blocks: [
      { type: "heading", text: "How a Playbook runs" },
      {
        type: "ordered",
        items: [
          "You type a request in chat — \"make me a campaign\", \"variations of this\", etc.",
          "AskMoji classifies your intent and picks the right Playbook.",
          "Each step runs in sequence with a status card on the canvas — Researching, Drafting strategy, Generating images, etc.",
          "At a checkpoint, the Playbook pauses and asks you to approve, modify, or reject the output.",
          "After approval, the next phase runs (for example, adapting your hero image to story and feed formats).",
        ],
      },
      {
        type: "screenshot",
        caption: "Playbook running on canvas — status cards on the left, generated images appearing on canvas as they complete.",
        src: "/docs/playbook-running.png",
        aspect: "wide",
      },
      { type: "heading", text: "Brand Campaign Launch (Campaign V2)" },
      {
        type: "para",
        text: "Triggered by phrases like \"make a campaign\", \"launch ads\", or \"create ads for [product]\".",
      },
      {
        type: "list",
        items: [
          "Pulls brand context from Heartbeat and your saved brand profile.",
          "Researches winning competitor ads from the Meta Ad Library.",
          "Generates a strategy brief with 4 ad concepts.",
          "Produces 5–6 hero images using AI image generation.",
          "Pauses for your approval — pick the best, request edits, or regenerate.",
          "Adapts approved hero image into platform formats (1:1 feed, 9:16 story, 4:5 feed).",
        ],
      },
      { type: "heading", text: "Ad Variations (Variations V2)" },
      {
        type: "para",
        text: "Triggered by \"make variations\", \"split test this\", \"remix this ad\", or by selecting an ad and clicking the variations button.",
      },
      {
        type: "list",
        items: [
          "Analyzes the source ad — copy, visual style, angle, hook.",
          "Picks a variation strategy: copy swap, angle change, style remix, or all three.",
          "Generates 3–6 new variants on the canvas.",
          "Pauses for your approval.",
        ],
      },
      { type: "heading", text: "Competitor Ad Clone Batch" },
      {
        type: "para",
        text:
          "Triggered when you reference a competitor ad — for example, \"clone these 5 competitor ads with my product\". Pulls competitor ad images, replaces the product or person with your own, and lays the results out on the canvas.",
      },
      {
        type: "screenshot",
        caption: "Approval checkpoint — generated images on canvas, action bar at the bottom asking for thumbs up or feedback.",
        src: "/docs/playbook-checkpoint.png",
        aspect: "wide",
      },
      { type: "heading", text: "Approving and iterating" },
      {
        type: "para",
        text:
          "When a checkpoint appears, you have three choices: Approve (continue to the next phase), Modify (give feedback in chat — \"the second one is great but make the background warmer\"), or Reject (regenerate from scratch).",
      },
      {
        type: "tip",
        text:
          "Use the Mark tool to draw on a specific image during a checkpoint, then approve. The Playbook will treat the marks as edit instructions for the next phase.",
      },
    ],
  },

  {
    id: "heartbeat",
    title: "Heartbeat — your brand brain",
    icon: Activity,
    group: "Features",
    intro:
      "Heartbeat is the long-term memory the AI uses to know your brand. It stores brand voice, offers, audiences, winning creative patterns, and performance signals.",
    blocks: [
      { type: "heading", text: "What's inside Heartbeat" },
      {
        type: "list",
        items: [
          "Brand DNA — voice, tone, value proposition, positioning, audiences.",
          "Daily performance signals — yesterday's spend, ROAS, conversion lift across accounts.",
          "Weekly synthesis — distilled insights from the last 7 days of activity.",
          "Creative events — wins, losses, fatigue, and trends spotted in your ads.",
        ],
      },
      {
        type: "screenshot",
        caption: "Heartbeat list view showing knowledge chunks grouped by category.",
        src: "/docs/heartbeat-list.png",
        aspect: "wide",
      },
      { type: "heading", text: "Three ways to view it" },
      {
        type: "feature",
        title: "Graph view",
        description: "Visual knowledge graph — nodes are facts, edges are relationships. Useful for spotting clusters and gaps.",
      },
      {
        type: "feature",
        title: "List view",
        description: "Sorted, filterable list of every chunk. Filter by category, importance, or freshness.",
      },
      {
        type: "feature",
        title: "Timeline view",
        description: "Chronological feed of when each chunk was added or updated. Useful for auditing what changed when.",
      },
      { type: "heading", text: "Adding knowledge manually" },
      {
        type: "ordered",
        items: [
          "Open Heartbeat from the left sidebar.",
          "Click + Add chunk.",
          "Pick a category (brand voice, offer, audience, etc.).",
          "Type or paste the content. Markdown is supported.",
          "Set importance (low/medium/high) — high-importance chunks weigh more in retrieval.",
        ],
      },
      {
        type: "tip",
        text:
          "Heartbeat is brand-scoped. Switch brands in the sidebar and you'll see a different knowledge graph for each.",
      },
      { type: "heading", text: "How AskMoji uses it" },
      {
        type: "para",
        text:
          "Every time you talk to AskMoji or kick off a playbook, it retrieves the most relevant Heartbeat chunks for your brand and uses them to ground the response. This is why your second campaign feels smarter than your first — the AI now knows your voice, audience, and what's already working.",
      },
    ],
  },

  {
    id: "brands",
    title: "Brands & products",
    icon: Tag,
    group: "Features",
    intro:
      "Brands are the top-level container in AdMoji. Everything — products, campaigns, Heartbeat, saved angles — sits under a brand.",
    blocks: [
      { type: "heading", text: "Creating a brand" },
      {
        type: "ordered",
        items: [
          "Open the Brands view from the left sidebar.",
          "Click New brand.",
          "Enter the brand name and (optionally) the brand website URL.",
          "If you provide a URL, AdMoji scrapes the site to auto-populate brand voice, products, and visuals.",
          "Connect a Meta ad account to enable analytics and campaign launches.",
        ],
      },
      {
        type: "screenshot",
        caption: "Brand list view — each card shows logo, status, and connected accounts.",
        src: "/docs/brands-list.png",
        aspect: "wide",
      },
      { type: "heading", text: "Brand intelligence" },
      {
        type: "list",
        items: [
          "Top performing ads — auto-imported from connected ad accounts, ranked by ROAS.",
          "Saved angles — proven hooks and angles you've used before.",
          "Competitor library — competitors you've added or AdMoji has discovered.",
        ],
      },
      { type: "heading", text: "Products" },
      {
        type: "para",
        text:
          "Products live under a brand. Each product has a name, description, hero image, and optional pricing. The AI uses product data when generating ads — \"make a campaign for the espresso machine\" pulls the product card and uses its image and copy.",
      },
      {
        type: "ordered",
        items: [
          "Open a brand and click Products.",
          "Click + Add product or import from a URL (Shopify product pages work well).",
          "Add hero images — these become the source images for AI generation.",
          "Tag the product with relevant audiences and offers.",
        ],
      },
      {
        type: "screenshot",
        caption: "Product detail view with hero image, description, and saved angles.",
        src: "/docs/product-detail.png",
        aspect: "wide",
      },
      {
        type: "tip",
        text:
          "Saved angles are gold. After a winning ad, click Save angle on the canvas — next time you make a campaign, AskMoji can reuse it.",
      },
    ],
  },

  {
    id: "ad-library",
    title: "Ad Library",
    icon: LayoutGrid,
    group: "Features",
    intro:
      "Ad Library is your inspiration vault. Browse templates, search competitor ads in the Meta Ad Library, and pull organic posts from social.",
    blocks: [
      { type: "heading", text: "Three tabs" },
      {
        type: "feature",
        title: "Ad Templates",
        description: "Curated templates by niche, format, and style. Filter by industry, sort by newest. Click any template to clone onto your canvas.",
      },
      {
        type: "feature",
        title: "Find Ads",
        description: "Search the Meta Ad Library for any brand. View their currently running ads, save favorites to your boards, or clone onto your canvas as a starting point.",
      },
      {
        type: "feature",
        title: "Organic Posts",
        description: "Browse high-engagement organic social posts as inspiration for paid creative.",
      },
      {
        type: "screenshot",
        caption: "Ad Library with the three tabs at the top and a masonry grid of ads.",
        src: "/docs/ad-library.png",
        aspect: "wide",
      },
      { type: "heading", text: "Cloning to canvas" },
      {
        type: "ordered",
        items: [
          "Hover an ad and click the clone icon.",
          "The image lands on your canvas as a new layer.",
          "From there, run a Variations playbook to generate your own riffs, or use Mark to swap product/copy.",
        ],
      },
      {
        type: "tip",
        text:
          "Saving an ad to a Board is different from cloning. Boards are for inspiration and team review. Cloning puts the ad on your active canvas.",
      },
      { type: "heading", text: "Search and filter" },
      {
        type: "list",
        items: [
          "Search box — keyword search across ad copy and brand names.",
          "Niche filter — narrow to a vertical (skincare, SaaS, fitness, etc.).",
          "Format filter — image, video, carousel.",
          "Style filter — UGC, studio, lifestyle, infographic, etc.",
          "Sort — newest, oldest, most engaged.",
        ],
      },
    ],
  },

  {
    id: "ads-manager",
    title: "Ads Manager",
    icon: Megaphone,
    group: "Features",
    intro:
      "Ads Manager is where you browse and operate live campaigns across connected ad platforms.",
    blocks: [
      { type: "heading", text: "Hierarchy" },
      {
        type: "para",
        text:
          "Like Meta Ads Manager, AdMoji uses a three-level hierarchy: Campaign → Ad Set → Ad. Each level has its own performance metrics and status controls.",
      },
      {
        type: "screenshot",
        caption: "Ads Manager view — campaigns table with spend, ROAS, status, and platform badges.",
        src: "/docs/ads-manager.png",
        aspect: "wide",
      },
      { type: "heading", text: "What you can do" },
      {
        type: "list",
        items: [
          "Filter campaigns by status (active, paused, completed).",
          "Search by campaign name.",
          "Sort by spend, ROAS, conversions, or any metric column.",
          "Click a campaign to drill into its ad sets, then click an ad set to see individual ads.",
          "Toggle status on/off without leaving the page.",
          "Select multiple campaigns for bulk actions.",
        ],
      },
      { type: "heading", text: "Launching from AdMoji" },
      {
        type: "para",
        text:
          "Once you've approved a campaign generated by the Campaign Playbook, you can publish it directly to your connected Meta account from this view. The campaign appears here within seconds of launch.",
      },
      {
        type: "tip",
        text:
          "Use the date range picker (top right) to compare performance across windows — this week vs last week, last 7 days vs prior 7 days, etc.",
      },
    ],
  },

  {
    id: "analytics",
    title: "Analytics",
    icon: BarChart2,
    group: "Features",
    intro:
      "Analytics is your unified dashboard across all connected ad platforms. One view for spend, ROAS, conversions, and creative-level performance.",
    blocks: [
      { type: "heading", text: "Top-level metrics" },
      {
        type: "list",
        items: [
          "Spend — total ad spend across selected accounts and date range.",
          "Impressions, Clicks, CTR — top-of-funnel volume metrics.",
          "Conversions, Revenue, ROAS — bottom-of-funnel results.",
          "CPC, CPM, Cost-per-result — efficiency metrics.",
          "Platform-specific metrics like Meta's Quality Ranking or TikTok's Completion Rate.",
        ],
      },
      {
        type: "screenshot",
        caption: "Analytics dashboard with metric cards at top and a campaign performance table below.",
        src: "/docs/analytics.png",
        aspect: "wide",
      },
      { type: "heading", text: "Filters" },
      {
        type: "feature",
        title: "Date range",
        description: "Presets (last 7 days, 30 days, this month, last month) or pick a custom range. Includes compare-to-previous toggle.",
      },
      {
        type: "feature",
        title: "Account selector",
        description: "Multi-account picker. Combine multiple Meta or TikTok accounts into one view.",
      },
      {
        type: "feature",
        title: "Attribution window",
        description: "1d-click, 7d-click, 28d-click, 1d-view, 7d-view, or 7d-click + 1d-view. Affects which conversions get credited.",
      },
      {
        type: "feature",
        title: "Platform filter",
        description: "Show or hide individual platforms in the rollup.",
      },
      { type: "heading", text: "Export" },
      {
        type: "para",
        text:
          "Click Export → CSV from the campaign table to download a spreadsheet of campaigns, ad sets, or ads with all their metrics. Useful for client reports or deeper analysis in Excel.",
      },
      {
        type: "tip",
        text:
          "Ask AskMoji \"what's working this week?\" while in Analytics view — it'll read the same metrics you're looking at and explain what to do next.",
      },
    ],
  },

  {
    id: "meta-ads",
    title: "Meta Ads integration",
    icon: ExternalLink,
    group: "Features",
    intro:
      "Connect your Meta (Facebook/Instagram) ad account to sync campaigns, run analytics, and launch ads directly from AdMoji.",
    blocks: [
      { type: "heading", text: "Connecting an account" },
      {
        type: "ordered",
        items: [
          "Open Meta Connections from the canvas sidebar (Bottom icons → Connections), or visit /meta/connections.",
          "Click Connect Meta account.",
          "Sign in to Facebook and grant the permissions AdMoji requests (ads management, page management, business read).",
          "Pick which ad account(s) and pages you want to sync.",
          "Click Save — the first sync begins immediately and usually finishes in 1–2 minutes.",
        ],
      },
      {
        type: "screenshot",
        caption: "Meta Connections page with connected accounts and the Connect new account button.",
        src: "/docs/meta-connections.png",
        aspect: "wide",
      },
      { type: "heading", text: "What gets synced" },
      {
        type: "list",
        items: [
          "Campaigns, ad sets, ads — full hierarchy with status and budget.",
          "Performance metrics — spend, impressions, clicks, conversions, ROAS, by day.",
          "Creative assets — ad images and copy stored in your Brand Intelligence.",
          "Pages and Instagram accounts you have access to (used for launching ads).",
        ],
      },
      { type: "heading", text: "Permissions and security" },
      {
        type: "list",
        items: [
          "AdMoji uses Meta's official Marketing API — no scraping or stored passwords.",
          "You can disconnect at any time from /meta/connections or your Meta Business settings.",
          "We never post on your behalf without explicit approval (e.g., when you publish an ad).",
        ],
      },
      {
        type: "warn",
        text:
          "If your Meta token expires (every 60 days for some account types), you'll see a banner in Analytics asking you to reconnect. The reconnect flow takes about 30 seconds.",
      },
    ],
  },

  {
    id: "boards",
    title: "Boards",
    icon: Bookmark,
    group: "Features",
    intro:
      "Boards are inspiration collections — a place to save competitor ads, internal references, and creative directions for later.",
    blocks: [
      { type: "heading", text: "Creating a board" },
      {
        type: "ordered",
        items: [
          "Visit /boards or click Boards from the sidebar.",
          "Click New board, name it, and pick a cover image.",
          "Add ads from the Ad Library — click the bookmark icon on any ad and pick the board.",
          "Share the board URL with your team for review.",
        ],
      },
      {
        type: "screenshot",
        caption: "Boards index page with cover-image cards in a masonry layout.",
        src: "/docs/boards.png",
        aspect: "wide",
      },
      { type: "heading", text: "Boards vs Canvas" },
      {
        type: "para",
        text:
          "Boards are read-only collections — they don't change. The Canvas is your active workspace where you generate, edit, and iterate. Use boards to research and share, then clone the best inspirations onto your canvas to start working.",
      },
      {
        type: "tip",
        text:
          "Open a board, then ask AskMoji \"what's the common pattern in these ads?\" — it'll analyze the board and pull out the shared hooks, visuals, and angles.",
      },
    ],
  },

  {
    id: "creative-editor",
    title: "Creative editor",
    icon: ImageIcon,
    group: "Features",
    intro:
      "The creative editor lets you make pixel-level edits to any image on the canvas — masking, inpainting, drawing, erasing, and text replacement.",
    blocks: [
      { type: "heading", text: "Opening the editor" },
      {
        type: "ordered",
        items: [
          "Click an image on the canvas.",
          "In the image toolbar that appears, click the Edit icon.",
          "The full-screen editor opens with the image centered.",
        ],
      },
      {
        type: "screenshot",
        caption: "Creative editor with brush tool active and a partial mask drawn on the image.",
        src: "/docs/creative-editor.png",
        aspect: "wide",
      },
      { type: "heading", text: "Tools" },
      {
        type: "feature",
        title: "Brush",
        description: "Paint a mask over the area you want to change. Brush size is adjustable.",
      },
      {
        type: "feature",
        title: "Eraser",
        description: "Erase part of the mask if you over-painted.",
      },
      {
        type: "feature",
        title: "Text-aware select",
        description: "Click on any visible text in the image — the editor auto-masks just that text region. Useful for swapping headlines.",
      },
      {
        type: "feature",
        title: "Generate",
        description: "Type a prompt describing the change. The AI replaces the masked area with the new content while preserving the rest.",
      },
      { type: "heading", text: "Inpainting tips" },
      {
        type: "list",
        items: [
          "Be specific in your prompt: \"replace headline with 'Save 30% This Weekend' in white sans-serif\" beats \"new text\".",
          "Match the surrounding style — if the original is photographic, the prompt should ask for photographic.",
          "If you don't love the result, undo and try a different mask shape — masks that are too tight can constrain the AI.",
        ],
      },
      {
        type: "tip",
        text:
          "For full-image regenerations (not just a region), use the Mark tool on the canvas instead — it triggers a full pipeline instead of an inpaint.",
      },
    ],
  },

  {
    id: "profile",
    title: "Profile & settings",
    icon: User,
    group: "Account",
    intro: "Your account profile — timezone, connections, and logout.",
    blocks: [
      { type: "heading", text: "Profile tab" },
      {
        type: "list",
        items: [
          "Email — read-only, your account identifier.",
          "Timezone — picks the timezone used for analytics dates and scheduled actions. Auto-detected from your first connected ad account on signup.",
          "Logout — sign out of all sessions in this browser.",
        ],
      },
      {
        type: "screenshot",
        caption: "Profile page showing timezone selector and connection list.",
        src: "/docs/profile.png",
        aspect: "wide",
      },
      { type: "heading", text: "Connections tab" },
      {
        type: "para",
        text:
          "Manage all third-party connections in one place — Meta, Google, TikTok, Shopify, etc. Connect, reconnect, or remove access.",
      },
      {
        type: "tip",
        text:
          "Changing timezones updates how dates are grouped in Analytics. If your numbers shift unexpectedly after a timezone change, that's why.",
      },
    ],
  },

  {
    id: "billing",
    title: "Billing & credits",
    icon: CreditCard,
    group: "Account",
    intro: "Plans, credits, and how usage is tracked.",
    blocks: [
      { type: "heading", text: "How credits work" },
      {
        type: "para",
        text:
          "Every paid AI action — image generation, AI chat, web scraping, embeddings — costs credits. Each plan grants a monthly credit balance. Your balance is shown in the top-right of the chat panel.",
      },
      {
        type: "screenshot",
        caption: "Credit balance indicator in the chat panel.",
        src: "/docs/credits-balance.png",
      },
      { type: "heading", text: "What costs credits" },
      {
        type: "list",
        items: [
          "AI image generation — varies by model (e.g., text-to-image cheaper than image edit).",
          "AskMoji chat replies — based on token usage of the underlying LLM.",
          "Playbook runs — sum of every step (research + chat + generation).",
          "Web scraping — competitor research, brand site imports.",
          "Embedding & retrieval — adding chunks to Heartbeat.",
        ],
      },
      { type: "heading", text: "Changing plans" },
      {
        type: "ordered",
        items: [
          "Visit /billing/select-plan or click your credit balance and pick Change plan.",
          "Select the new plan and confirm.",
          "Credits adjust on your next billing cycle.",
        ],
      },
      {
        type: "note",
        text:
          "Running out of credits doesn't immediately block actions — there's a 1.5× grace buffer. Once you exceed that, AskMoji will ask you to upgrade or wait until your next renewal.",
      },
    ],
  },

  {
    id: "shortcuts",
    title: "Keyboard shortcuts",
    icon: Keyboard,
    group: "Account",
    intro: "Speed up canvas work with these shortcuts.",
    blocks: [
      { type: "heading", text: "Canvas navigation" },
      { type: "shortcut", keys: ["Space"], description: "Hold to temporarily activate Hand (pan) tool." },
      { type: "shortcut", keys: ["Cmd", "+"], description: "Zoom in." },
      { type: "shortcut", keys: ["Cmd", "-"], description: "Zoom out." },
      { type: "shortcut", keys: ["Cmd", "0"], description: "Fit to screen." },
      { type: "shortcut", keys: ["1"], description: "Switch to Select tool." },
      { type: "shortcut", keys: ["2"], description: "Switch to Hand tool." },
      { type: "shortcut", keys: ["3"], description: "Switch to Brush tool." },
      { type: "shortcut", keys: ["4"], description: "Switch to Comment tool." },
      { type: "shortcut", keys: ["5"], description: "Switch to Mark tool." },

      { type: "heading", text: "Selection" },
      { type: "shortcut", keys: ["Click"], description: "Select an image." },
      { type: "shortcut", keys: ["Cmd", "Click"], description: "Add to selection." },
      { type: "shortcut", keys: ["Delete"], description: "Delete selected images." },
      { type: "shortcut", keys: ["Backspace"], description: "Delete selected images." },
      { type: "shortcut", keys: ["Shift", "Drag"], description: "Free-position drag (no snapping)." },

      { type: "heading", text: "Chat & search" },
      { type: "shortcut", keys: ["Cmd", "K"], description: "Open documentation search (this page)." },
      { type: "shortcut", keys: ["Cmd", "/"], description: "Focus the chat input." },
      { type: "shortcut", keys: ["↑"], description: "In chat, recall the last message you sent." },
    ],
  },

  {
    id: "tips",
    title: "Tips for best results",
    icon: Lightbulb,
    group: "Account",
    intro: "Lessons from teams that get the most out of AdMoji.",
    blocks: [
      { type: "heading", text: "Feed Heartbeat first" },
      {
        type: "para",
        text:
          "Spend the first 15 minutes adding your brand voice, top offers, and audience to Heartbeat. Every AI output gets dramatically better once it has this context.",
      },
      { type: "heading", text: "Talk like a media buyer" },
      {
        type: "para",
        text:
          "AskMoji is tuned to think like a senior media buyer. Speak the same way: \"swap the hook to a fear-of-missing-out angle\", \"the first creative is fatigued, replace it with a UGC variant\", \"compare last week to the prior week on ROAS\".",
      },
      { type: "heading", text: "Use Marks instead of long descriptions" },
      {
        type: "para",
        text:
          "Drawing a box on an image with a one-line note (\"swap this product\") is faster and more accurate than describing the change in chat.",
      },
      { type: "heading", text: "Save winning angles" },
      {
        type: "para",
        text:
          "When a campaign wins, save the angle to your brand. Future Playbook runs will reuse it as a starting point — your second campaign should be faster and better than your first.",
      },
      { type: "heading", text: "Let Playbooks finish before iterating" },
      {
        type: "para",
        text:
          "Playbooks have approval checkpoints by design. Wait for the checkpoint, then iterate — interrupting mid-run wastes credits.",
      },
    ],
  },

  {
    id: "faq",
    title: "FAQ & troubleshooting",
    icon: HelpCircle,
    group: "Account",
    intro: "Common questions and how to fix the most frequent issues.",
    blocks: [
      { type: "heading", text: "Why didn't my generation start?" },
      {
        type: "list",
        items: [
          "Check your credit balance — you may be at the 1.5× grace limit.",
          "Check that you have a brand selected in the sidebar.",
          "Refresh the chat panel — sometimes a stale connection blocks new requests.",
        ],
      },
      { type: "heading", text: "My Meta data is out of date" },
      {
        type: "para",
        text:
          "Visit Meta Connections and click Sync now. If the issue persists, your token may have expired — disconnect and reconnect to refresh it.",
      },
      { type: "heading", text: "Generated images don't look like my brand" },
      {
        type: "list",
        items: [
          "Add brand voice and visual examples to Heartbeat.",
          "Upload reference product photos as your brand's hero images.",
          "Use the Mark tool to point out exactly what to change.",
        ],
      },
      { type: "heading", text: "How do I delete a project?" },
      {
        type: "para",
        text:
          "In the sidebar, hover the project name → click the three-dot menu → Delete. The project, its chat history, and its canvas images are removed permanently.",
      },
      { type: "heading", text: "Can I export my canvas?" },
      {
        type: "para",
        text:
          "Yes — select images on the canvas and use the export button in the image toolbar. Single images export as PNG; multi-select exports as a ZIP.",
      },
      { type: "heading", text: "Need more help?" },
      {
        type: "para",
        text:
          "Type \"help\" or your question into AskMoji — it can answer most questions about the product itself, and will hand off to support if it can't.",
      },
    ],
  },
];

const GROUPS = ["Get Started", "Workspace", "Features", "Account"];

// ---------- Helper components ----------

function Screenshot({ caption, src, aspect = "video" }: { caption: string; src?: string; aspect?: "video" | "wide" | "square" }) {
  const [errored, setErrored] = useState(false);
  const aspectClass =
    aspect === "wide" ? "aspect-[16/9]" : aspect === "square" ? "aspect-square" : "aspect-video";
  const showImage = !!src && !errored;

  return (
    <figure className="my-6">
      <div
        className={`${aspectClass} w-full rounded-xl overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 relative shadow-sm`}
      >
        {showImage ? (
          <img
            src={src}
            alt={caption}
            onError={() => setErrored(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-6">
            <Camera className="w-10 h-10 mb-3 opacity-60" />
            <div className="text-xs uppercase tracking-wider font-medium mb-1">Screenshot</div>
            <div className="text-sm text-center text-gray-500 max-w-md">{caption}</div>
            {src && (
              <div className="mt-3 text-[11px] font-mono text-gray-400">Add image at {src}</div>
            )}
          </div>
        )}
      </div>
      <figcaption className="mt-2 text-xs text-gray-500 text-center italic">{caption}</figcaption>
    </figure>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 flex gap-3">
      <Lightbulb className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
      <div className="text-sm text-indigo-900 leading-relaxed">{children}</div>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-lg border border-gray-200 bg-gray-50 p-4 flex gap-3">
      <CheckCircle2 className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="text-sm text-amber-900 leading-relaxed">{children}</div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 text-xs font-medium font-mono rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm">
      {children}
    </kbd>
  );
}

function Shortcut({ keys, description }: { keys: string[]; description: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0">
      <span className="text-sm text-gray-700">{description}</span>
      <div className="flex items-center gap-1">
        {keys.map((key, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-xs text-gray-400">+</span>}
            <Kbd>{key}</Kbd>
          </span>
        ))}
      </div>
    </div>
  );
}

function Feature({ title, description }: { title: string; description: string }) {
  return (
    <div className="my-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className="font-semibold text-gray-900 text-sm mb-1">{title}</div>
      <div className="text-sm text-gray-600 leading-relaxed">{description}</div>
    </div>
  );
}

function BlockRenderer({ block }: { block: Block }) {
  switch (block.type) {
    case "para":
      return <p className="text-[15px] text-gray-700 leading-relaxed my-4">{block.text}</p>;
    case "heading":
      return (
        <h3 className="text-xl font-semibold text-gray-900 mt-10 mb-3 scroll-mt-24">{block.text}</h3>
      );
    case "list":
      return (
        <ul className="my-4 space-y-2 text-[15px] text-gray-700 leading-relaxed">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-gray-400 shrink-0 mt-1.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "ordered":
      return (
        <ol className="my-4 space-y-2 text-[15px] text-gray-700 leading-relaxed">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="pt-0.5">{item}</span>
            </li>
          ))}
        </ol>
      );
    case "screenshot":
      return <Screenshot caption={block.caption} src={block.src} aspect={block.aspect} />;
    case "tip":
      return <Tip>{block.text}</Tip>;
    case "note":
      return <Note>{block.text}</Note>;
    case "warn":
      return <Warn>{block.text}</Warn>;
    case "shortcut":
      return <Shortcut keys={block.keys} description={block.description} />;
    case "feature":
      return <Feature title={block.title} description={block.description} />;
    case "divider":
      return <hr className="my-8 border-gray-200" />;
    default:
      return null;
  }
}

// ---------- Search ----------

type SearchResult = {
  sectionId: string;
  sectionTitle: string;
  groupName: string;
  snippet: string;
  matchType: "title" | "intro" | "heading" | "body";
};

function buildSearchableText(section: Section): { text: string; headings: string[] } {
  const headings: string[] = [];
  const parts: string[] = [section.title, section.intro];
  for (const block of section.blocks) {
    if (block.type === "heading") {
      headings.push(block.text);
      parts.push(block.text);
    } else if (block.type === "para") parts.push(block.text);
    else if (block.type === "list" || block.type === "ordered") parts.push(block.items.join(" "));
    else if (block.type === "tip" || block.type === "note" || block.type === "warn") parts.push(block.text);
    else if (block.type === "feature") parts.push(`${block.title} ${block.description}`);
    else if (block.type === "shortcut") parts.push(block.description);
    else if (block.type === "screenshot") parts.push(block.caption);
  }
  return { text: parts.join(" ").toLowerCase(), headings };
}

function search(query: string): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: SearchResult[] = [];
  for (const section of SECTIONS) {
    const { text, headings } = buildSearchableText(section);
    if (section.title.toLowerCase().includes(q)) {
      results.push({
        sectionId: section.id,
        sectionTitle: section.title,
        groupName: section.group,
        snippet: section.intro,
        matchType: "title",
      });
      continue;
    }
    const headingMatch = headings.find(h => h.toLowerCase().includes(q));
    if (headingMatch) {
      results.push({
        sectionId: section.id,
        sectionTitle: section.title,
        groupName: section.group,
        snippet: headingMatch,
        matchType: "heading",
      });
      continue;
    }
    if (section.intro.toLowerCase().includes(q)) {
      results.push({
        sectionId: section.id,
        sectionTitle: section.title,
        groupName: section.group,
        snippet: section.intro,
        matchType: "intro",
      });
      continue;
    }
    const idx = text.indexOf(q);
    if (idx !== -1) {
      const start = Math.max(0, idx - 40);
      const end = Math.min(text.length, idx + q.length + 80);
      const snippet = (start > 0 ? "…" : "") + text.slice(start, end) + (end < text.length ? "…" : "");
      results.push({
        sectionId: section.id,
        sectionTitle: section.title,
        groupName: section.group,
        snippet,
        matchType: "body",
      });
    }
  }
  return results.slice(0, 8);
}

// ---------- Main page ----------

export default function Documentation() {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => search(query), [query]);

  // Cmd+K shortcut to focus search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Scroll-spy: track which section is currently in view and highlight in sidebar.
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.id;
          if (id) setActiveId(id);
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
      setSearchOpen(false);
      setQuery("");
    }
  }, []);

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
          <button
            onClick={() => setLocation("/canvas")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Canvas</span>
          </button>

          <div className="h-6 w-px bg-gray-200 hidden sm:block" />

          <Link
            href="/documentation"
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 shrink-0"
          >
            <Sparkles className="w-4 h-4 text-indigo-600" />
            <span>AdMoji Documentation</span>
          </Link>

          {/* Search */}
          <div className="flex-1 max-w-xl relative ml-auto">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={e => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                placeholder="Search docs…"
                className="w-full h-9 pl-9 pr-12 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:bg-white transition"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-[10px] text-gray-400 pointer-events-none">
                <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </div>
            </div>

            {searchOpen && query && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden z-50 max-h-[60vh] overflow-y-auto">
                {results.length === 0 ? (
                  <div className="p-6 text-sm text-gray-500 text-center">
                    No results for <span className="font-mono text-gray-700">"{query}"</span>
                  </div>
                ) : (
                  <ul>
                    {results.map((r, i) => (
                      <li key={i}>
                        <button
                          onMouseDown={e => {
                            e.preventDefault();
                            scrollToSection(r.sectionId);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 group"
                        >
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-0.5">
                            <span>{r.groupName}</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-indigo-600 font-medium">{r.sectionTitle}</span>
                          </div>
                          <div className="text-sm text-gray-700 line-clamp-2">{r.snippet}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto py-8 pr-4">
          <nav className="space-y-6">
            {GROUPS.map(group => {
              const sectionsInGroup = SECTIONS.filter(s => s.group === group);
              const collapsed = collapsedGroups.has(group);
              return (
                <div key={group}>
                  <button
                    onClick={() => toggleGroup(group)}
                    className="flex items-center gap-1.5 w-full text-left text-[11px] uppercase tracking-wider font-semibold text-gray-500 hover:text-gray-700 mb-2 px-2"
                  >
                    {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {group}
                  </button>
                  {!collapsed && (
                    <ul className="space-y-0.5">
                      {sectionsInGroup.map(section => {
                        const Icon = section.icon;
                        const isActive = activeId === section.id;
                        return (
                          <li key={section.id}>
                            <button
                              onClick={() => scrollToSection(section.id)}
                              className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
                                isActive
                                  ? "bg-indigo-50 text-indigo-700 font-medium"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-indigo-600" : "text-gray-400"}`} />
                              <span className="truncate">{section.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main ref={contentRef} className="flex-1 min-w-0 px-4 sm:px-8 py-10 max-w-3xl">
          {SECTIONS.map((section, idx) => {
            const Icon = section.icon;
            return (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-20 mb-16"
              >
                {idx === 0 && (
                  <div className="mb-2 text-xs uppercase tracking-wider font-semibold text-indigo-600">
                    AdMoji Documentation
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{section.title}</h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-2">{section.intro}</p>

                <div>
                  {section.blocks.map((block, i) => (
                    <BlockRenderer key={i} block={block} />
                  ))}
                </div>

                {idx < SECTIONS.length - 1 && (
                  <hr className="mt-16 border-gray-100" />
                )}
              </section>
            );
          })}

          {/* Footer nav */}
          <footer className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="hover:text-gray-900 transition-colors"
              >
                ↑ Back to top
              </button>
              <button
                onClick={() => setLocation("/canvas")}
                className="hover:text-gray-900 transition-colors flex items-center gap-1.5"
              >
                Back to Canvas
                <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </button>
            </div>
          </footer>
        </main>

        {/* Right-side mini-TOC for current section's headings */}
        <RightToc activeId={activeId} />
      </div>
    </div>
  );
}

// On-page table of contents for the current section's headings.
function RightToc({ activeId }: { activeId: string }) {
  const section = SECTIONS.find(s => s.id === activeId);
  const headings = section?.blocks.filter((b): b is Extract<Block, { type: "heading" }> => b.type === "heading") || [];

  if (headings.length === 0) return null;

  return (
    <aside className="hidden xl:block w-56 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto py-10 pl-6">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-3">
        On this page
      </div>
      <ul className="space-y-1.5 border-l border-gray-200 pl-3">
        {headings.map((h, i) => (
          <li key={i}>
            <span className="text-sm text-gray-600 hover:text-indigo-600 cursor-default block py-0.5 leading-snug">
              {h.text}
            </span>
          </li>
        ))}
      </ul>
    </aside>
  );
}
